import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "progress-content" }
const _hoisted_2 = { class: "progress-title" }
const _hoisted_3 = { class: "elapsed-time" }
const _hoisted_4 = { class: "progress-bar" }
const _hoisted_5 = { class: "progress-text" }

import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useTemplateProgressStore } from '@/store/templateProgressStore';
import { useRouter } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'TemplateProgressTopNotification',
  setup(__props) {

const progressStore = useTemplateProgressStore();
const router = useRouter();

const hasActiveOperations = computed(() => progressStore.hasActiveOperations);
const isMobile = ref(
  typeof window !== 'undefined' ? window.innerWidth <= 768 : false
);
const currentOperation = computed(() => {
  const operations = progressStore.getAllActiveOperations;
  return operations.length > 0 ? operations[0] : null;
});

function updateIsMobile() {
  isMobile.value = window.innerWidth <= 768;
}

// Elapsed time logic
const elapsedTime = ref('');
let intervalId: ReturnType<typeof setInterval> | null = null;

function formatElapsedTime(startTime: number, endTime: number) {
  const totalSeconds = Math.floor((endTime - startTime) / 1000);
  if (totalSeconds < 0) return '00:00';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
}

watch(
  currentOperation,
  (op) => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    if (op) {
      const update = () => {
        const end = op.endTime || Date.now();
        elapsedTime.value = formatElapsedTime(op.startTime, end);
      };

      update();

      if (!op.endTime) {
        intervalId = setInterval(update, 1000);
      }
    } else {
      elapsedTime.value = '';
    }
  },
  { deep: true, immediate: true }
);

if (typeof window !== 'undefined') {
  window.addEventListener('resize', updateIsMobile);
}

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateIsMobile);
  }
});

// Show the notification everywhere when there are active operations
const shouldShowNotification = computed(() => {
  if (isMobile.value && currentOperation.value) {
    return false;
  }
  return hasActiveOperations.value && progressStore.showNotifications;
});

function handleClick() {
  if (!currentOperation.value) {
    console.log('[ProgressNotification] No current operation, skipping click');
    return;
  }

  console.log('[ProgressNotification] Click detected, operation:', {
    templateId: currentOperation.value.templateId,
    versionId: currentOperation.value.versionId
  });

  const route = router.currentRoute.value;
  const currentProjectId = getCurrentProjectId();

  console.log(
    '[ProgressNotification] Current route:',
    route.path,
    'projectID:',
    currentProjectId
  );

  // Case 1: Already on a templates-related route - just dispatch the open event
  if (
    route.path.includes('/templates-view/') ||
    route.path.includes('/templates/')
  ) {
    console.log(
      '[ProgressNotification] Case 1: On templates view, dispatching openTemplateEdit'
    );
    progressStore.requestOpenTemplate(
      currentOperation.value.templateId,
      currentOperation.value.versionId
    );
    return;
  }

  // Case 2: Inside P1 project page (tabs) – switch tab via custom event
  if (route.path.includes('/p1/') && route.params.projectID) {
    console.log('[ProgressNotification] Case 2: Switching to templates tab');
    const detail = {
      templateId: currentOperation.value.templateId,
      versionId: currentOperation.value.versionId
    };
    window.dispatchEvent(
      new CustomEvent('switchToTemplatesTabAndOpen', { detail })
    );
    return;
  }

  // Case 3: On other project routes (catalog, etc) - navigate to templates view
  if (currentProjectId && currentProjectId !== 'default') {
    console.log('[ProgressNotification] Case 3: Navigating to templates view');
    router.push(`/templates/${currentProjectId}`);
    setTimeout(() => {
      if (currentOperation.value) {
        progressStore.requestOpenTemplate(
          currentOperation.value.templateId,
          currentOperation.value.versionId
        );
      }
    }, 450);
    return;
  }

  // Case 4: Fallback - navigate to p1 templates view
  console.log('[ProgressNotification] Case 4: Fallback navigation');
  router.push(`/p1/templates-view/${currentProjectId}`);
  setTimeout(() => {
    if (currentOperation.value) {
      progressStore.requestOpenTemplate(
        currentOperation.value.templateId,
        currentOperation.value.versionId
      );
    }
  }, 450);
}

function dismiss() {
  progressStore.hideNotifications();
}

function getCurrentProjectId(): string {
  const route = router.currentRoute.value;
  return (route.params.projectID as string) || 'default';
}

return (_ctx: any,_cache: any) => {
  return (shouldShowNotification.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "progress-top-notification",
        onClick: handleClick
      }, [
        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "progress-icon" }, [
          _createElementVNode("i", { class: "fa fa-cog fa-spin" })
        ], -1)),
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", null, _toDisplayString(currentOperation.value?.type === 'edit' ? 'Editing' : 'Improving') + " Template", 1),
            _createElementVNode("span", _hoisted_3, _toDisplayString(elapsedTime.value), 1)
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: "progress-fill",
              style: _normalizeStyle({ width: `${currentOperation.value?.progress || 0}%` })
            }, null, 4)
          ]),
          _createElementVNode("div", _hoisted_5, _toDisplayString(currentOperation.value?.message), 1),
          _cache[0] || (_cache[0] = _createElementVNode("div", { class: "click-hint" }, "Click to view progress", -1))
        ]),
        _createElementVNode("button", {
          class: "close-btn",
          onClick: _withModifiers(dismiss, ["stop"])
        }, "×")
      ]))
    : _createCommentVNode("", true)
}
}

})